import React, { useState } from "react";
import { Lock, Loader2, ShieldCheck, KeyRound, Copy, AlertTriangle, HelpCircle, Eye, EyeOff } from "lucide-react";
import { useCrypto } from "../context/CryptoContext";
import { useAuth } from "../context/AuthContext";
import { Crest } from "./Media";
import EncryptionInfo from "./EncryptionInfo";

const Input = ({ type, "data-testid": testId, ...props }) => {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  return (
    <div className="relative">
      <input
        {...props}
        data-testid={testId}
        type={isPw ? (show ? "text" : "password") : type}
        className={`bg-[#0A0A0A] border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body ${isPw ? "pr-11" : ""}`}
      />
      {isPw && (
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          data-testid={testId ? `${testId}-toggle` : "password-toggle"}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#14A89A] transition-colors"
        >
          {show ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
        </button>
      )}
    </div>
  );
};

export function RecoveryPhrase({ phrase, onDone }) {
  const [ack, setAck] = useState(false);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0A0A0A]/95 backdrop-blur-md" data-testid="recovery-phrase-modal">
      <div className="bg-[#121212] border border-[#14A89A]/40 w-full max-w-md p-8">
        <ShieldCheck className="text-[#14A89A] mb-4" strokeWidth={1.5} />
        <h2 className="font-heading text-2xl text-white mb-2">Your recovery phrase</h2>
        <p className="font-body text-sm text-zinc-400 mb-6">These 12 words are the <span className="text-white">only</span> way to recover your encrypted vault if you lose your password or device. Write them down and store them offline. We can never recover them for you.</p>
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="recovery-words">
          {phrase.split(" ").map((w, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-white/10 px-2 py-2 text-center">
              <span className="text-zinc-600 text-[10px] mr-1">{i + 1}</span>
              <span className="font-body text-sm text-[#14A89A]">{w}</span>
            </div>
          ))}
        </div>
        <button onClick={() => navigator.clipboard.writeText(phrase)} className="flex items-center gap-2 text-zinc-400 hover:text-white text-xs font-body mb-6"><Copy size={14} /> Copy to clipboard</button>
        <label className="flex items-start gap-2 mb-6 cursor-pointer">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="recovery-ack-checkbox" className="mt-1 accent-[#14A89A]" />
          <span className="font-body text-xs text-zinc-300">I have safely stored my recovery phrase. I understand it cannot be recovered if lost.</span>
        </label>
        <button onClick={onDone} disabled={!ack} data-testid="recovery-done-button" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors font-body disabled:opacity-40">Continue</button>
      </div>
    </div>
  );
}

export default function CryptoGate() {
  const { status, setupKeys, unlockWithPassword, unlockWithRecovery, recoveryPhrase, setRecoveryPhrase } = useCrypto();
  const { logout } = useAuth();
  const [pw, setPw] = useState("");
  const [mode, setMode] = useState("password"); // password | recovery
  const [phrase, setPhrase] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(false);

  if (recoveryPhrase) {
    return <RecoveryPhrase phrase={recoveryPhrase} onDone={() => setRecoveryPhrase(null)} />;
  }
  if (status !== "needSetup" && status !== "needUnlock") return null;

  const doSetup = async () => {
    setBusy(true); setError("");
    try { await setupKeys(pw); }
    catch (e) { setError("Could not enable encryption. " + (e.message || "")); }
    finally { setBusy(false); }
  };
  const doUnlock = async () => {
    setBusy(true); setError("");
    try {
      if (mode === "password") {
        const ok = await unlockWithPassword(pw);
        if (!ok) setError("No keys found for this account.");
      } else {
        await unlockWithRecovery(phrase, newPw);
      }
    } catch (e) { setError(mode === "password" ? "Wrong password for your encryption vault." : "Invalid recovery phrase."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0A0A0A] backdrop-blur-md" data-testid="crypto-gate">
      <div className="w-full max-w-sm">
        <Crest className="h-12 w-12 mb-6" />
        {status === "needSetup" ? (
          <>
            <Lock className="text-[#14A89A] mb-4" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl text-white mb-2">Enable encryption</h2>
            <p className="font-body text-sm text-zinc-400 mb-6">Sovereign Signal is end-to-end encrypted. We'll generate your private keys on this device. Confirm your password to begin.</p>
            <Input type="password" placeholder="Your account password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="crypto-setup-password" />
            {error && <p className="text-red-400 text-sm mt-3 font-body" data-testid="crypto-error">{error}</p>}
            <button onClick={doSetup} disabled={busy || !pw} data-testid="crypto-setup-button" className="mt-6 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><ShieldCheck size={18} /> Generate my keys</>}
            </button>
          </>
        ) : (
          <>
            <KeyRound className="text-[#14A89A] mb-4" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl text-white mb-2">Unlock your vault</h2>
            <p className="font-body text-sm text-zinc-400 mb-6">This device needs to unlock your encrypted keys.</p>
            <div className="flex gap-6 border-b border-white/10 mb-6">
              <button onClick={() => { setMode("password"); setError(""); }} className={`pb-2 font-body text-xs tracking-wider uppercase ${mode === "password" ? "text-white border-b-2 border-[#14A89A]" : "text-zinc-500"}`}>Password</button>
              <button onClick={() => { setMode("recovery"); setError(""); }} data-testid="use-recovery-tab" className={`pb-2 font-body text-xs tracking-wider uppercase ${mode === "recovery" ? "text-white border-b-2 border-[#14A89A]" : "text-zinc-500"}`}>Recovery phrase</button>
            </div>
            {mode === "password" ? (
              <Input type="password" placeholder="Your account password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="crypto-unlock-password" />
            ) : (
              <div className="space-y-3">
                <textarea rows={3} placeholder="Enter your 12-word recovery phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="recovery-phrase-input" className="bg-[#0A0A0A] border border-white/15 px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body resize-none" />
                <Input type="password" placeholder="Set a new password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="recovery-new-password" />
                <p className="flex items-start gap-2 text-[10px] text-zinc-500 font-body"><AlertTriangle size={12} className="mt-0.5 text-[#14A89A]" /> Note: this re-wraps your keys; your login password is unchanged.</p>
              </div>
            )}
            {error && <p className="text-red-400 text-sm mt-3 font-body" data-testid="crypto-error">{error}</p>}
            <button onClick={doUnlock} disabled={busy} data-testid="crypto-unlock-button" className="mt-6 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Unlock"}
            </button>
          </>
        )}
        <button onClick={() => setInfo(true)} data-testid="how-encryption-works" className="mt-4 flex items-center justify-center gap-2 text-zinc-500 hover:text-[#14A89A] text-xs font-body w-full transition-colors">
          <HelpCircle size={14} /> How does this encryption work?
        </button>
        <button onClick={logout} className="mt-4 text-zinc-600 hover:text-white text-xs font-body w-full text-center">Disconnect</button>
      </div>
      <EncryptionInfo open={info} onClose={() => setInfo(false)} />
    </div>
  );
}
